import { CurrencyCode } from "@asmbl/shared/constants";
import { annualToHourly, hourlyToAnnual } from "@asmbl/shared/currency";
import { Money, multiply, roundMoney } from "@asmbl/shared/money";
import { TableCell, Tooltip } from "@mui/material";
import makeStyles from "../../../makeStylesV4Compat";
import { useEffect, useState } from "react";
import { NumberFormatValues, SourceInfo } from "react-number-format";
import { CompUnit, RecItemInput } from "src/__generated__/graphql";
import { useCompStructure } from "src/components/CompStructureContext";
import { useCurrencies } from "../../CurrenciesContext";
import { CashInput, PercentInput } from "./ConfigurableTargetInputCell";
import { DogEarWithTooltip } from "./DogEarWithTooltip";

interface Props {
  onChange: (
    newSalary: Money | undefined,
    unitType: CompUnit | null
  ) => unknown;
  value: RecItemInput | undefined;
  basePay: { annualCashEquivalent: Money; hourlyCashEquivalent: Money } | null;
  payCurrencyCode: CurrencyCode | null;
  isHourly?: boolean;
  disabled?: boolean;
  disableTooltip?: boolean;
  hasUnpublishedChanges?: boolean;
  hideDogEar?: boolean;
}

const useStyles = makeStyles(() => ({
  inputCell: {
    position: "relative",
    "&$inputCell": {
      padding: 0,
    },
  },
  inputContainer: { display: "flex", justifyContent: "flex-end" },
}));

export function SalaryInputCell({
  value,
  basePay,
  payCurrencyCode,
  onChange,
  disabled = false,
  hasUnpublishedChanges = false,
  isHourly = false,
  disableTooltip = false,
  hideDogEar = false,
}: Props): JSX.Element {
  const classes = useStyles();
  const { defaultCurrencyCode } = useCurrencies();
  const { compStructure } = useCompStructure();
  const currency =
    basePay?.annualCashEquivalent.currency ??
    payCurrencyCode ??
    defaultCurrencyCode;
  const workingHoursPerYear = compStructure?.workingHoursPerYear ?? 0;
  const absolute =
    value == null
      ? undefined
      : calculateItemCompValues(value, basePay, workingHoursPerYear);
  const [absoluteInput, setAbsoluteInput] = useState<number | undefined>(
    absolute?.annualCashEquivalent
  );

  const [hourlyInput, setHourlyInput] = useState<number | undefined>(
    absolute?.hourlyCashEquivalent
  );

  const [percentInput, setPercentInput] = useState<number | undefined>(
    absolute?.percentValue
  );

  // Handle situations where the value prop changes after the component mounts
  useEffect(() => {
    if (absolute) {
      setAbsoluteInput(absolute.annualCashEquivalent);
      setHourlyInput(absolute.hourlyCashEquivalent);
      setPercentInput(absolute.percentValue);
    }
  }, [absolute, absoluteInput, basePay]);

  const resetInputValues = () => {
    setAbsoluteInput(undefined);
    setPercentInput(undefined);
    setHourlyInput(undefined);
    onChange(undefined, null);
  };

  const handleAbsolute = (
    { floatValue }: NumberFormatValues,
    { source }: SourceInfo
  ) => {
    if (source !== "event") {
      return;
    }

    if (floatValue === undefined) {
      resetInputValues();
    } else {
      setAbsoluteInput(floatValue);
      setPercentInput(
        basePay !== null
          ? calculatePercent(basePay.annualCashEquivalent, floatValue)
          : undefined
      );
      if (isHourly) {
        setHourlyInput(
          compStructure?.workingHoursPerYear
            ? convertToHourly(floatValue, compStructure.workingHoursPerYear)
            : undefined
        );
      }

      if (floatValue !== value?.recommendedCashValue?.value) {
        onChange({ value: floatValue, currency }, CompUnit.CASH);
      }
    }
  };

  const handleHourly = (
    { floatValue }: NumberFormatValues,
    { source }: SourceInfo
  ) => {
    if (source !== "event") {
      return;
    }

    if (floatValue === undefined) {
      resetInputValues();
    } else {
      setHourlyInput(floatValue);
      if (workingHoursPerYear) {
        setAbsoluteInput(floatValue * workingHoursPerYear);
        setPercentInput(
          basePay !== null
            ? calculatePercent(basePay.hourlyCashEquivalent, floatValue)
            : undefined
        );
      }
      if (floatValue !== value?.recommendedCashValue?.value) {
        onChange(
          {
            value: floatValue,
            currency,
          },
          CompUnit.HOURLY_CASH
        );
      }
    }
  };

  const handlePercent = (
    { floatValue }: NumberFormatValues,
    { source }: SourceInfo
  ) => {
    if (source !== "event") {
      return;
    }

    if (floatValue === undefined) {
      resetInputValues();
    } else {
      setPercentInput(floatValue);

      const newAbsolute =
        basePay && basePay.annualCashEquivalent.value !== 0
          ? Math.round(basePay.annualCashEquivalent.value * (floatValue / 100))
          : 0;
      setAbsoluteInput(newAbsolute);
      setHourlyInput(newAbsolute / workingHoursPerYear);
      if (newAbsolute !== value?.recommendedCashValue?.value) {
        onChange({ value: newAbsolute, currency }, CompUnit.CASH);
      }
    }
  };

  const salaryCell = isHourly ? (
    <CashInput
      disabled={disabled}
      cashInput={hourlyInput}
      handleChange={handleHourly}
      currency={currency}
      isHourly={isHourly}
    />
  ) : (
    <CashInput
      disabled={disabled}
      cashInput={absoluteInput}
      handleChange={handleAbsolute}
      currency={currency}
    />
  );

  const secondCell = isHourly ? (
    <CashInput
      disabled={disabled}
      cashInput={absoluteInput}
      handleChange={handleAbsolute}
      currency={currency}
    />
  ) : (
    <PercentInput
      handleChange={handlePercent}
      disabled={disabled || !basePay}
      percentInput={percentInput}
    />
  );
  return (
    <TableCell role="gridcell" className={classes.inputCell} colSpan={3}>
      {disabled ? (
        <Tooltip
          title={disableTooltip ? "" : "Please select a new position first"}
          placement="top"
        >
          <div className={classes.inputContainer}>
            <>
              {hasUnpublishedChanges &&
                value?.recommendedCashValue != null &&
                !hideDogEar && <DogEarWithTooltip />}
              {salaryCell}
            </>
            {secondCell}
          </div>
        </Tooltip>
      ) : (
        <div className={classes.inputContainer}>
          <>
            {hasUnpublishedChanges &&
              value?.recommendedCashValue != null &&
              !hideDogEar && <DogEarWithTooltip />}
            {salaryCell}
          </>
          {secondCell}
        </div>
      )}
    </TableCell>
  );
}

function convertToHourly(floatValue: number, workingHoursPerYear: number) {
  return Math.round((floatValue / workingHoursPerYear) * 10_000) / 10_000;
}

function calculatePercent(basePay: Money | null, absolute: number): number {
  return basePay && basePay.value !== 0
    ? Math.round((absolute / basePay.value) * 10000) / 100
    : 0;
}

function calculateItemCompValues(
  item: RecItemInput,
  basePay: { annualCashEquivalent: Money; hourlyCashEquivalent: Money } | null,
  workingHoursPerYear: number
): {
  annualCashEquivalent: number | undefined;
  hourlyCashEquivalent: number | undefined;
  percentValue: number | undefined;
} {
  const { recommendedCashValue, recommendedPercentValue } = item;

  if (!recommendedCashValue && recommendedPercentValue === 0) {
    return {
      annualCashEquivalent: 0,
      hourlyCashEquivalent: 0,
      percentValue: 0,
    };
  }

  if (recommendedPercentValue && basePay) {
    const annualIncrease = roundMoney(
      multiply(basePay.annualCashEquivalent, recommendedPercentValue / 100),
      1
    );

    return {
      hourlyCashEquivalent: annualToHourly(workingHoursPerYear, annualIncrease)
        .value,
      annualCashEquivalent: roundMoney(annualIncrease, 1).value,
      percentValue: recommendedPercentValue,
    };
  }

  if (!recommendedCashValue) {
    return {
      annualCashEquivalent: undefined,
      hourlyCashEquivalent: undefined,
      percentValue: undefined,
    };
  }

  const annualIncrease =
    item.unitType === CompUnit.HOURLY_CASH
      ? {
          value: Math.round(
            hourlyToAnnual(workingHoursPerYear, recommendedCashValue).value
          ),
          currency: recommendedCashValue.currency,
        }
      : recommendedCashValue;

  return {
    hourlyCashEquivalent:
      item.unitType === CompUnit.HOURLY_CASH
        ? recommendedCashValue.value
        : +annualToHourly(
            workingHoursPerYear,
            recommendedCashValue
          ).value.toFixed(2),

    annualCashEquivalent: roundMoney(annualIncrease, 1).value,
    percentValue: basePay?.annualCashEquivalent
      ? calculatePercent(basePay.annualCashEquivalent, annualIncrease.value)
      : undefined,
  };
}
